import { useState } from 'react';
import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { KeyValue, Pill, Spinner, FormGrid, Field, TextInput, NumberInput, LineTable, SelectInput } from '../../components/ui';
import { SearchSelect } from '../../components/SearchSelect';
import { useDetail } from '../../lib/useDetail';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { can } from '../../lib/permissions';
import { money, num, planStatus } from '../../lib/format';
import type { ProcessTemplate, ProcessTemplateOperation, Operation } from '../../lib/types';

// 核心服务写接口返回 { data: payload } 信封；防御性解包，兼容个别直接返回 payload 的历史写法。
function unwrap<T>(body: unknown): T {
  if (body && typeof body === 'object' && 'data' in (body as Record<string, unknown>)) {
    return (body as { data: T }).data;
  }
  return body as T;
}

const CURRENCY_OPTIONS = ['ARS', 'USD', 'EUR', 'CNY'];

interface OpRow {
  operationId: string; label: string; sort: string; minutes: string; rate: string;
  workstation: string; currency: string; suspect: boolean; qc: boolean; description: string;
}

const emptyOp = (): OpRow => ({ operationId: '', label: '', sort: '', minutes: '', rate: '', workstation: '', currency: 'ARS', suspect: false, qc: false, description: '' });

function opRowsFromTemplate(t: ProcessTemplate): OpRow[] {
  return (t.operations ?? []).map((o) => ({
    operationId: o.operation_id,
    label: `${o.operation_code ?? ''} ${o.operation_name_zh ?? ''}`.trim(),
    sort: String(o.sort_order ?? ''),
    minutes: o.planned_minutes == null ? '' : String(o.planned_minutes),
    rate: o.hourly_cost == null ? '' : String(o.hourly_cost),
    workstation: o.workstation ?? '',
    currency: o.cost_currency || 'ARS',
    suspect: !!o.currency_suspect,
    qc: !!o.qc_required,
    description: o.description ?? '',
  }));
}

function opPayload(rows: OpRow[]) {
  return rows.filter((o) => o.operationId).map((o, index) => {
    const body: Record<string, unknown> = {
      operation_id: o.operationId,
      sort_order: o.sort === '' ? index : Number(o.sort),
      planned_minutes: o.minutes === '' ? 0 : Number(o.minutes),
      qc_required: o.qc,
      cost_currency: o.currency || 'ARS',
      currency_suspect: o.suspect,
    };
    if (o.rate !== '') body.hourly_cost = Number(o.rate);
    if (o.workstation) body.workstation = o.workstation;
    if (o.description) body.description = o.description;
    return body;
  });
}

// 新建 / 编辑草稿（仅草稿且未被引用可编辑；已批准/停用请复制为新版本再改）。
function TemplateForm({ initial, onDone, onCancel }: { initial: ProcessTemplate | null; onDone: () => void; onCancel: () => void }) {
  const [code, setCode] = useState(initial?.code ?? '');
  const [nameZh, setNameZh] = useState(initial?.name_zh ?? '');
  const [nameEs, setNameEs] = useState(initial?.name_es ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [rows, setRows] = useState<OpRow[]>(initial ? opRowsFromTemplate(initial) : [emptyOp()]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const setRow = (i: number, patch: Partial<OpRow>) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const submit = async () => {
    setErr(null);
    if (!code.trim()) { setErr('请填写模板编码'); return; }
    if (!nameZh.trim()) { setErr('请填写模板名称'); return; }
    const ops = opPayload(rows);
    if (!ops.length) { setErr('至少需要一道有效工序'); return; }
    setBusy(true);
    try {
      const payload = { code: code.trim(), name_zh: nameZh.trim(), name_es: nameEs || null, description: description || null, operations: ops };
      if (initial) unwrap<ProcessTemplate>(await api.put(`/api/process-templates/${initial.id}`, payload));
      else unwrap<ProcessTemplate>(await api.post('/api/process-templates', payload));
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '保存失败');
    } finally { setBusy(false); }
  };

  return (
    <section className="panel" style={{ marginBottom: 'var(--gap)' }}>
      <div className="panel-head"><div><h2>{initial ? `编辑工艺模板草稿 · ${initial.code}` : '新建工艺模板'}</h2>
        <div className="desc">写入 /api/process-templates · 工序只带工时与费率（工费 = 工时/60 × 费率，全部以 ARS 计），车床/磨床/抛光等耗材不进材料方案。新建保存为草稿，批准后方可用于新工单。</div></div></div>
      <div className="panel-body">
        {err ? <div className="notice bad" style={{ marginBottom: 12 }}>{err}</div> : null}
        <FormGrid>
          <Field label="模板编码" required><TextInput value={code} onChange={(e) => setCode(e.target.value)} placeholder="如 PT-LASER-CLAD" /></Field>
          <Field label="名称（中）" required><TextInput value={nameZh} onChange={(e) => setNameZh(e.target.value)} /></Field>
          <Field label="名称（西语）"><TextInput value={nameEs} onChange={(e) => setNameEs(e.target.value)} /></Field>
          <Field label="描述" span><TextInput value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
        </FormGrid>

        <div className="detail-head" style={{ margin: '14px 0 6px' }}><h3 style={{ fontSize: 14 }}>工序序列</h3><div className="spacer" />
          <button className="btn link" onClick={() => setRows((rs) => [...rs, emptyOp()])}>+ 添加工序</button>
        </div>
        <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>排序号越小越靠前；费率留空则回落到工序主档默认费率。币种默认 ARS；历史误标（如 ERPNext 标成 USD）勾选“疑似币种”仅保留原值、不自动乘汇率。</div>
        {rows.map((o, i) => (
          <div key={i} className="form-row" style={{ display: 'grid', gridTemplateColumns: '2.2fr 0.7fr 0.8fr 0.9fr 0.8fr 0.7fr auto auto auto', gap: 8, marginBottom: 6, alignItems: 'end' }}>
            <Field label={`工序 ${i + 1}`}>
              <SearchSelect<Operation> endpoint="/api/operations" value={o.operationId || null} label={o.label}
                onChange={(row) => setRow(i, { operationId: row?.id ?? '', label: row ? `${row.code} ${row.name_zh}` : '', minutes: row ? String(row.default_minutes ?? '') : o.minutes, rate: row ? String(row.hourly_cost ?? '') : o.rate, workstation: row?.workstation ?? o.workstation, qc: row ? !!row.qc_required : o.qc })}
                optionTitle={(it) => `${it.code} · ${it.name_zh}`} placeholder="检索工序…" />
            </Field>
            <Field label="排序"><NumberInput value={o.sort} onChange={(e) => setRow(i, { sort: e.target.value })} /></Field>
            <Field label="工时(分)"><NumberInput value={o.minutes} onChange={(e) => setRow(i, { minutes: e.target.value })} /></Field>
            <Field label="费率(ARS/h)"><NumberInput value={o.rate} onChange={(e) => setRow(i, { rate: e.target.value })} /></Field>
            <Field label="工位"><TextInput value={o.workstation} onChange={(e) => setRow(i, { workstation: e.target.value })} /></Field>
            <Field label="币种">
              <SelectInput value={o.currency} onChange={(e) => setRow(i, { currency: e.target.value })}>
                {CURRENCY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </SelectInput>
            </Field>
            <label style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: 12, paddingBottom: 8 }} title="历史误标币种：保留原值不折算">
              <input type="checkbox" checked={o.suspect} onChange={(e) => setRow(i, { suspect: e.target.checked })} />疑似
            </label>
            <label style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: 12, paddingBottom: 8 }}>
              <input type="checkbox" checked={o.qc} onChange={(e) => setRow(i, { qc: e.target.checked })} />质检
            </label>
            <button className="btn" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} title="删除该工序">×</button>
          </div>
        ))}

        <div className="detail-actions">
          <button className="btn primary" onClick={submit} disabled={busy}>{busy ? '提交中…' : (initial ? '保存草稿' : '创建草稿')}</button>
          <button className="btn" onClick={onCancel} disabled={busy}>取消</button>
        </div>
      </div>
    </section>
  );
}

function TemplateDetail({ row, onChanged, onEdit }: { row: ProcessTemplate; onChanged: () => void; onEdit: (t: ProcessTemplate) => void }) {
  const { user } = useAuth();
  const canCreate = can(user, 'process-templates', 'create');
  const canTransition = can(user, 'process-templates', 'transition');
  const { data, loading, error, reload } = useDetail<ProcessTemplate>(`/api/process-templates/${row.id}`);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const t = data ?? row;
  const ops = t.operations ?? [];
  const used = !!t.used;
  const editable = canCreate && t.status === 'draft' && !used;

  const act = async (kind: 'approve' | 'deactivate' | 'copy') => {
    setErr(null); setBusy(kind);
    try {
      if (kind === 'copy') unwrap<ProcessTemplate>(await api.post(`/api/process-templates/${t.id}/copy`, {}));
      else unwrap<ProcessTemplate>(await api.post(`/api/process-templates/${t.id}/status`, { status: kind === 'approve' ? 'approved' : 'deactivated' }));
      reload(); onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '操作失败');
    } finally { setBusy(null); }
  };

  const machineEstimate = ops.reduce((sum, o) => {
    const rate = o.hourly_cost == null ? (Number(o.default_hourly_cost) || 0) : Number(o.hourly_cost);
    return sum + (Number(o.planned_minutes) || 0) / 60 * rate;
  }, 0);

  return (
    <div className="detail" onClick={(e) => e.stopPropagation()}>
      <div className="detail-head">
        <span className="id">{t.code}</span>
        <h3>{t.name_zh}</h3>
        <Pill tone={planStatus(t.status).tone} label={planStatus(t.status).label} />
        <Pill tone={used ? 'info' : 'neutral'} label={used ? '已被工单引用' : '未引用'} />
        <div className="spacer" />
        {loading ? <Spinner label="刷新…" /> : null}
      </div>
      {error ? <div className="notice bad">{error}</div> : null}
      {err ? <div className="notice bad">{err}</div> : null}
      <div className="kv-grid">
        <KeyValue k="版本">v{t.version}{t.parent_id ? '（派生）' : ''}</KeyValue>
        <KeyValue k="工序数">{ops.length}</KeyValue>
        <KeyValue k="机时费估算(ARS)">{money(machineEstimate, 'ARS')}</KeyValue>
        <KeyValue k="西语名称">{t.name_es || '—'}</KeyValue>
        <KeyValue k="描述">{t.description || '—'}</KeyValue>
        <KeyValue k="批准时间">{t.approved_at ? t.approved_at.slice(0, 10) : '—'}</KeyValue>
      </div>
      <div className="k" style={{ fontSize: 11, color: 'var(--ink-3)', margin: '10px 0 4px' }}>工序序列（工费 = 工时/60 × 费率；工单建单时冻结）</div>
      <LineTable<ProcessTemplateOperation>
        rows={ops}
        emptyText="暂无工序"
        columns={[
          { header: '序', align: 'right', render: (o) => <span className="num">{o.sort_order}</span> },
          { header: '工序', render: (o) => `${o.operation_code ?? ''} ${o.operation_name_zh ?? ''}`.trim() || '—' },
          { header: '工位', render: (o) => o.workstation || '—' },
          { header: '工时(分)', align: 'right', render: (o) => <span className="num">{num(o.planned_minutes, 0)}</span> },
          { header: '费率', align: 'right', render: (o) => <span className="num">{o.hourly_cost == null ? `默认 ${money(o.default_hourly_cost, o.cost_currency || o.default_cost_currency || 'ARS')}` : money(o.hourly_cost, o.cost_currency || 'ARS')}{(o.currency_suspect ?? o.default_currency_suspect) ? <span className="muted"> ⚠</span> : null}</span> },
          { header: '质检', render: (o) => (o.qc_required ? <Pill tone="warn" label="需质检" /> : <span className="muted">—</span>) },
        ]}
      />
      {canCreate || canTransition ? (
        <div className="detail-actions" style={{ marginTop: 10 }}>
          {editable ? <button className="btn" onClick={() => onEdit(t)}>编辑草稿</button> : null}
          {canTransition && t.status === 'draft' ? <button className="btn primary" onClick={() => act('approve')} disabled={!!busy}>{busy === 'approve' ? '提交中…' : '批准'}</button> : null}
          {canTransition && t.status === 'approved' ? <button className="btn" onClick={() => act('deactivate')} disabled={!!busy}>{busy === 'deactivate' ? '提交中…' : '停用'}</button> : null}
          {canCreate ? <button className="btn" onClick={() => act('copy')} disabled={!!busy}>{busy === 'copy' ? '提交中…' : (used ? '复制为新版本' : '复制版本')}</button> : null}
        </div>
      ) : null}
      {editable ? <div className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>已批准版本或被工单引用的版本不可直接覆盖，请用“复制为新版本”后修改。</div> : null}
    </div>
  );
}

export function ProcessTemplatesPage() {
  const { user } = useAuth();
  const [reloadSignal, setReloadSignal] = useState(0);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ProcessTemplate | null>(null);
  const canCreate = can(user, 'process-templates', 'create');

  const columns: Column<ProcessTemplate>[] = [
    { header: '编码', render: (t) => <span className="num strong">{t.code}</span> },
    { header: '名称', render: (t) => t.name_zh },
    { header: '版本', render: (t) => <span className="muted">v{t.version}</span> },
    { header: '工序数', align: 'right', render: (t) => <span className="num">{t.operation_count ?? 0}</span> },
    { header: '状态', render: (t) => <Pill tone={planStatus(t.status).tone} label={planStatus(t.status).label} /> },
    { header: '引用', render: (t) => (t.used ? <Pill tone="info" label="已引用" /> : <span className="muted">—</span>) },
  ];

  const closeAndReload = () => { setCreating(false); setEditing(null); setReloadSignal((n) => n + 1); };

  return (
    <>
      {creating ? <TemplateForm initial={null} onDone={closeAndReload} onCancel={() => setCreating(false)} /> : null}
      {editing ? <TemplateForm initial={editing} onDone={closeAndReload} onCancel={() => setEditing(null)} /> : null}
      <ListPage<ProcessTemplate>
        title="工艺模板"
        desc="可复用工序序列 · /api/process-templates。生命周期 草稿→批准→停用；仅已批准可用于新工单，被引用版本只能复制为新版本再改。展开查看/维护工序、工时与费率（工费 ARS）"
        endpoint="/api/process-templates"
        columns={columns}
        rowId={(t) => t.id}
        showYear={false}
        searchPlaceholder="搜索模板编码 / 名称…"
        reloadSignal={reloadSignal}
        actions={canCreate ? (
          <button className="btn primary" onClick={() => { setEditing(null); setCreating((v) => !v); }}>{creating ? '收起新建' : '新建模板'}</button>
        ) : undefined}
        renderDetail={(row) => <TemplateDetail row={row} onChanged={() => setReloadSignal((n) => n + 1)} onEdit={(t) => { setCreating(false); setEditing(t); }} />}
      />
    </>
  );
}
